/**
 * The PaymentTokeniser backed by payments-core's hosted Finix card fields
 * (ORDR-55).
 *
 * THE TOKEN LIVES IN MEMORY FOR ONE CHECKOUT CALL. `tokenise` hands the TK and
 * its fraud session straight to checkout and keeps no copy. It is never logged,
 * stored, put in a URL or included in an error; the refusals below carry a code
 * and fixed words, never the capture's own message.
 *
 * NO RUNTIME IMPORT OF payments-core HERE, on purpose. This module is reachable
 * from the entry, and a value import would put the card capture into every
 * kitchen display's download. The Finix error is recognised by its `code`, and
 * the hook itself lives in the lazily loaded `CardFields` chunk.
 */

import type { FinixCardErrorCode, FinixCardToken } from "@pirouz-ca/payments-core";
import type { CardTokenisation, PaymentTokeniser } from "./customerServices.js";

/** What the tokeniser needs from the mounted card fields: `useCardFields().tokenize`. */
export interface CardCapture {
  tokenize(): Promise<FinixCardToken | null>;
}

/** Filled by the mounted card form, emptied when it unmounts. */
export interface CardCaptureSlot {
  current(): CardCapture | null;
  fill(capture: CardCapture): void;
  /** Empties the slot only if it still holds this capture. */
  release(capture: CardCapture): void;
}

export function createCardCaptureSlot(): CardCaptureSlot {
  let held: CardCapture | null = null;
  return {
    current: () => held,
    fill(capture) {
      held = capture;
    },
    release(capture) {
      if (held === capture) {
        held = null;
      }
    },
  };
}

/**
 * `card_not_captured` is the hosted hook's `null`: it swallows the Finix error
 * and reports its code on the card form, which names it there.
 */
export type CardRefusalCode = FinixCardErrorCode | "card_not_captured";

const MESSAGES: Record<CardRefusalCode, string> = {
  card_not_captured: "Your card details could not be confirmed. Check them and try again. Nothing has been charged.",
  tokenization_failed: "Your card could not be verified. Check the card details and try again. Nothing has been charged.",
  missing_token: "The card form did not return a payment token. Please try again. Nothing has been charged.",
  missing_fraud_session:
    "The secure payment check did not start, so your card was not sent. Reload the page and try again. Nothing has been charged.",
  sdk_unavailable: "The secure card form could not be loaded. Reload the page, or pay at the counter. Nothing has been charged.",
  not_mounted: "The secure card form is still loading. Wait a moment and try again. Nothing has been charged.",
};

export function cardRefusalMessage(code: CardRefusalCode): string {
  return MESSAGES[code];
}

export class CardNotCaptured extends Error {
  readonly code: CardRefusalCode;
  constructor(code: CardRefusalCode) {
    super(cardRefusalMessage(code));
    this.name = "CardNotCaptured";
    this.code = code;
  }
}

function finixCode(error: unknown): FinixCardErrorCode | null {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" && code !== "card_not_captured" && Object.hasOwn(MESSAGES, code)
    ? (code as FinixCardErrorCode)
    : null;
}

export function createCardTokeniser(slot: CardCaptureSlot): PaymentTokeniser {
  return {
    // The request is not read: the amount is the server quote's, and the card
    // is whatever the diner typed into Finix's iframe.
    async tokenise(): Promise<CardTokenisation> {
      const capture = slot.current();
      if (capture === null) {
        throw new CardNotCaptured("not_mounted");
      }
      let card: FinixCardToken | null;
      try {
        card = await capture.tokenize();
      } catch (error) {
        throw new CardNotCaptured(finixCode(error) ?? "tokenization_failed");
      }
      if (card === null) {
        throw new CardNotCaptured("card_not_captured");
      }
      if (card.source.type !== "CARD" || card.source.token === "") {
        throw new CardNotCaptured("missing_token");
      }
      if (card.fraudSessionId === "") {
        throw new CardNotCaptured("missing_fraud_session");
      }
      return { sourceType: "CARD", token: card.source.token, fraudSessionId: card.fraudSessionId };
    },
  };
}
